import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ams360Service, AMS360Document } from '../services/ams360Service';
import { useClickOutside } from '../hooks/useClickOutside';
import { formatWorkflowLabel } from '../constants/betaToolLabels';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;

interface Document {
  id: string;
  name: string;
  originalName: string;
  type: string;
  size?: number;
  uploadedAt: string;
  projectId?: string;
  projectName?: string;
  status?: string; // Processing status (uploaded, processing, completed, error)
  isAMS360?: boolean; // Flag to identify AMS360 documents
}

interface Report {
  id: string;
  title: string;
  workflowType: string;
  createdAt: string;
  documentNames?: string[];
  projectId?: string;
  projectName?: string;
}

interface PastDocumentSelectorProps {
  customerId: string;
  projectId?: string;
  ams360CustomerId?: string | null; // AMS360 customer GUID
  onDocumentsSelected: (documents: Document[]) => void;
  onReportsSelected: (reports: Report[]) => void;
  selectedDocuments?: Document[];
  selectedReports?: Report[];
}

export const PastDocumentSelector: React.FC<PastDocumentSelectorProps> = ({
  customerId,
  projectId,
  ams360CustomerId: propAms360CustomerId,
  onDocumentsSelected,
  onReportsSelected,
  selectedDocuments = [],
  selectedReports = []
}) => {
  const [pastDocuments, setPastDocuments] = useState<Document[]>([]);
  const [pastReports, setPastReports] = useState<Report[]>([]);
  const [ams360Documents, setAms360Documents] = useState<Document[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingAms360, setLoadingAms360] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showAms360, setShowAms360] = useState(false);
  const [ams360Error, setAms360Error] = useState<string | null>(null);
  const [customerAms360Id, setCustomerAms360Id] = useState<string | null>(propAms360CustomerId || null);

  const documentsDropdownRef = useRef<HTMLDivElement>(null);
  const reportsDropdownRef = useRef<HTMLDivElement>(null);
  const ams360DropdownRef = useRef<HTMLDivElement>(null);

  const closeDocuments = useCallback(() => setShowDocuments(false), []);
  const closeReports = useCallback(() => setShowReports(false), []);
  const closeAms360 = useCallback(() => setShowAms360(false), []);

  useClickOutside(documentsDropdownRef, showDocuments, closeDocuments);
  useClickOutside(reportsDropdownRef, showReports, closeReports);
  useClickOutside(ams360DropdownRef, showAms360, closeAms360);

  const token = localStorage.getItem('riskninja-token');

  // Fetch customer data to get AMS360 ID if not provided as prop
  useEffect(() => {
    const fetchCustomerAms360Id = async () => {
      if (propAms360CustomerId || !customerId || !token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const customer = await response.json();
          if (customer.ams360CustomerId) {
            setCustomerAms360Id(customer.ams360CustomerId);
          }
        }
      } catch (error) {
        console.error('Error fetching customer AMS360 ID:', error);
      }
    };

    fetchCustomerAms360Id();
  }, [customerId, propAms360CustomerId, token]);

  // Fetch past documents
  const fetchPastDocuments = async () => {
    if (!customerId || !token) return;

    setLoadingDocuments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const allDocuments = await response.json();
        // Only show documents that have completed processing - documents still in
        // uploaded/processing/error states won't have content available for AI analysis
        const completedDocuments = (allDocuments || []).filter(
          (doc: any) => doc.status === 'completed'
        );
        setPastDocuments(completedDocuments);
      } else {
        console.error('Failed to fetch past documents:', response.status);
      }
    } catch (error) {
      console.error('Error fetching past documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Fetch past reports
  const fetchPastReports = async () => {
    if (!customerId || !token) return;

    setLoadingReports(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const reports = await response.json();
        setPastReports(reports || []);
      } else {
        console.error('Failed to fetch past reports:', response.status);
      }
    } catch (error) {
      console.error('Error fetching past reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Fetch AMS360 documents
  const fetchAms360Documents = async () => {
    if (!customerId) return;

    setLoadingAms360(true);
    setAms360Error(null);
    try {
      const documents = await ams360Service.getDocuments(customerId);
      // Convert AMS360 documents to our Document interface
      const convertedDocs: Document[] = documents.map((doc: AMS360Document) => ({
        id: `ams360-${doc.documentId}`,
        name: doc.fileName,
        originalName: doc.fileName,
        type: doc.documentType || 'application/octet-stream',
        size: doc.fileSize,
        uploadedAt: doc.dateCreated || new Date().toISOString(),
        isAMS360: true
      }));
      setAms360Documents(convertedDocs);
    } catch (error: any) {
      console.error('Error fetching AMS360 documents:', error);
      setAms360Error(error.message || 'Failed to load AMS360 documents');
      setAms360Documents([]);
    } finally {
      setLoadingAms360(false);
    }
  };

  // Fetch data when component mounts or customerId changes
  useEffect(() => {
    if (customerId) {
      fetchPastDocuments();
      fetchPastReports();
    }
  }, [customerId]);

  // Handle document selection
  const toggleDocumentSelection = (document: Document) => {
    const isSelected = selectedDocuments.some(doc => doc.id === document.id);
    const newSelection = isSelected
      ? selectedDocuments.filter(doc => doc.id !== document.id)
      : [...selectedDocuments, document];
    
    onDocumentsSelected(newSelection);
  };

  // Handle report selection
  const toggleReportSelection = (report: Report) => {
    const isSelected = selectedReports.some(rep => rep.id === report.id);
    const newSelection = isSelected
      ? selectedReports.filter(rep => rep.id !== report.id)
      : [...selectedReports, report];
    
    onReportsSelected(newSelection);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type from name/type
  const getFileType = (document: Document) => {
    if (document.type === 'application/pdf') return 'PDF';
    if (document.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word';
    if (document.type === 'application/vnd.ms-excel' || document.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel';
    if (document.type === 'text/csv') return 'CSV';
    if (document.type === 'message/rfc822' || document.originalName.toLowerCase().endsWith('.eml') || document.originalName.toLowerCase().endsWith('.msg')) return 'Email';
    return 'Document';
  };

  return (
    <div className="flex gap-4">
      {/* Past Documents Dropdown */}
      <div className="relative" ref={documentsDropdownRef}>
        <button
          onClick={() => {
            if (!showDocuments && pastDocuments.length === 0) {
              fetchPastDocuments();
            }
            setShowDocuments(!showDocuments);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Past Documents
          {selectedDocuments.length > 0 && (
            <span className="ml-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
              {selectedDocuments.length}
            </span>
          )}
          <svg className={`w-4 h-4 transition-transform ${showDocuments ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDocuments && (
          <div className="absolute top-full left-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
            {loadingDocuments ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading documents...</p>
              </div>
            ) : pastDocuments.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No past documents found
              </div>
            ) : (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                  Select documents to include:
                </div>
                {pastDocuments.map((document) => {
                  const isSelected = selectedDocuments.some(doc => doc.id === document.id);
                  return (
                    <div
                      key={document.id}
                      onClick={() => toggleDocumentSelection(document)}
                      className={`p-2 rounded cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {document.originalName}
                            </p>
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                              {getFileType(document)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>{formatFileSize(document.size)}</span>
                            <span>•</span>
                            <span>{formatDate(document.uploadedAt)}</span>
                            {document.projectName && (
                              <>
                                <span>•</span>
                                <span className="font-medium">{document.projectName}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Past Reports Dropdown */}
      <div className="relative" ref={reportsDropdownRef}>
        <button
          onClick={() => {
            if (!showReports && pastReports.length === 0) {
              fetchPastReports();
            }
            setShowReports(!showReports);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Past Reports
          {selectedReports.length > 0 && (
            <span className="ml-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs px-2 py-1 rounded-full">
              {selectedReports.length}
            </span>
          )}
          <svg className={`w-4 h-4 transition-transform ${showReports ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showReports && (
          <div className="absolute top-full left-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
            {loadingReports ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading reports...</p>
              </div>
            ) : pastReports.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No past reports found
              </div>
            ) : (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                  Select reports to reference:
                </div>
                {pastReports.map((report) => {
                  const isSelected = selectedReports.some(rep => rep.id === report.id);
                  return (
                    <div
                      key={report.id}
                      onClick={() => toggleReportSelection(report)}
                      className={`p-2 rounded cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-green-600 border-green-600' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {report.title}
                            </p>
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                              {formatWorkflowLabel(report.workflowType)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>{formatDate(report.createdAt)}</span>
                            {report.documentNames && report.documentNames.length > 0 && (
                              <>
                                <span>•</span>
                                <span>{report.documentNames.length} document{report.documentNames.length !== 1 ? 's' : ''}</span>
                              </>
                            )}
                            {report.projectName && (
                              <>
                                <span>•</span>
                                <span className="font-medium">{report.projectName}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AMS360 Documents Dropdown - Only show if customer is linked to AMS360 */}
      {customerAms360Id && (
        <div className="relative" ref={ams360DropdownRef}>
          <button
            onClick={() => {
              // Always fetch fresh on open (on-demand, not cached)
              fetchAms360Documents();
              setShowAms360(!showAms360);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-md shadow-sm text-sm font-medium text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            AMS360 Documents
            {selectedDocuments.filter(d => d.isAMS360).length > 0 && (
              <span className="ml-1 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs px-2 py-1 rounded-full">
                {selectedDocuments.filter(d => d.isAMS360).length}
              </span>
            )}
            <svg className={`w-4 h-4 transition-transform ${showAms360 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAms360 && (
            <div className="absolute top-full left-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
              {loadingAms360 ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading AMS360 documents...</p>
                </div>
              ) : ams360Error ? (
                <div className="p-4 text-center">
                  <svg className="w-8 h-8 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-600 dark:text-red-400">{ams360Error}</p>
                  <button
                    onClick={() => fetchAms360Documents()}
                    className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : ams360Documents.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No documents found in AMS360
                </div>
              ) : (
                <div className="p-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Select AMS360 documents:
                  </div>
                  {ams360Documents.map((document) => {
                    const isSelected = selectedDocuments.some(doc => doc.id === document.id);
                    return (
                      <div
                        key={document.id}
                        onClick={() => toggleDocumentSelection(document)}
                        className={`p-2 rounded cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'bg-green-600 border-green-600'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {document.originalName}
                              </p>
                              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                AMS360
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              {document.size && <span>{formatFileSize(document.size)}</span>}
                              <span>•</span>
                              <span>{formatDate(document.uploadedAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PastDocumentSelector;